import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../useApi';
import { ErrorNotice } from '../components/ErrorNotice';

// The refund work queue.
//
// Cancelling a subscription does not refund anything — in Stripe those are two
// separate actions, deliberately. So a customer who cancels inside the window
// is owed money that no system will move on its own, and this is the only
// place that fact is visible. That is why the app tells customers to allow up
// to 7 days: a person has to read this list and act on it.

interface Cancellation {
  subscriptionId: string;
  customerId: string;
  customerName: string | null;
  customerEmail: string | null;
  accountId: string | null;
  startedAt: string | null;
  canceledAt: string | null;
  daysToCancel: number | null;
  currency: string;
  amountPaid: number;
  amountRefunded: number;
  action: 'refund-due' | 'refunded' | 'outside-window' | 'nothing-charged' | 'scheduled';
}

interface Report {
  cancellations: Cancellation[];
  refundWindowDays: number;
  truncated: boolean;
}

const ACTION_LABEL: Record<Cancellation['action'], string> = {
  'refund-due': 'Refund due',
  refunded: 'Refunded',
  'outside-window': 'No refund due',
  'nothing-charged': 'Never charged',
  scheduled: 'Canceling',
};

/** Stripe amounts are in the currency's smallest unit. */
function money(minorUnits: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(minorUnits / 100);
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function CancellationsPage() {
  const api = useApi();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    setError(null);
    try {
      setReport(await api.get<Report>('/support/cancellations'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cancellations.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const due = report?.cancellations.filter((c) => c.action === 'refund-due') ?? [];

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Cancellations</h1>
          <p className="muted">
            Refunds are issued by hand in the Stripe Dashboard — canceling a
            subscription never refunds anything by itself.
          </p>
        </div>
        <button type="button" className="link-button" onClick={() => void load()}>
          Refresh
        </button>
      </div>

      {error && <ErrorNotice message={error} />}
      {loading && !report && <p className="muted">Loading…</p>}

      {report && due.length > 0 && (
        <div className="notice warn">
          <strong>
            {due.length} refund{due.length === 1 ? '' : 's'} to process.
          </strong>{' '}
          These customers canceled within {report.refundWindowDays} days and have not
          been refunded. Issue each one in Stripe, then refresh.
        </div>
      )}

      {report && due.length === 0 && !loading && (
        <div className="notice success">Nothing waiting on a refund.</div>
      )}

      {report?.truncated && (
        <div className="notice">
          Only the most recent cancellations are shown — there are older ones this
          report did not load.
        </div>
      )}

      {report && report.cancellations.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Canceled</th>
              <th>Held for</th>
              <th>Paid</th>
              <th>Refunded</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {report.cancellations.map((c) => (
              <tr key={c.subscriptionId}>
                <td>
                  {c.customerName ?? c.customerEmail ?? c.customerId}
                  {c.customerName && c.customerEmail && (
                    <div className="muted small">{c.customerEmail}</div>
                  )}
                </td>
                <td>{formatDate(c.canceledAt)}</td>
                <td>{c.daysToCancel === null ? '—' : `${c.daysToCancel} days`}</td>
                <td>{money(c.amountPaid, c.currency)}</td>
                <td>{money(c.amountRefunded, c.currency)}</td>
                <td>
                  <span className={c.action === 'refund-due' ? 'status-warn' : 'muted'}>
                    {ACTION_LABEL[c.action]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {report && report.cancellations.length === 0 && !loading && (
        <p className="muted">No cancellations yet.</p>
      )}
    </>
  );
}
